import { useState } from 'react';
import { navigate } from '@/hooks/useRoute';
import './JournalPage.css';

type JournalCategory =
  | 'all'
  | 'style'
  | 'moissanite'
  | 'guides'
  | 'stories';

type Article = {
  id: string;
  category: Exclude<JournalCategory, 'all'>;
  categoryLabel: string;
  eyebrow: string;
  title: string;
  excerpt: string;
  image: string;
  readTime: string;
  content: string[];
};

const articles: Article[] = [
  {
    id: 'moissanite-vs-diamond',
    category: 'moissanite',
    categoryLabel: 'מוסונייט',
    eyebrow: 'MOISSANITE 101',
    title: 'מוסונייט או יהלום? מה באמת ההבדל',
    excerpt:
      'ברק, קשיות, מראה ומחיר — כל מה שכדאי לדעת לפני שבוחרים.',
    image: '/assets/all-moissanite-hero.png',
    readTime: '5 דקות קריאה',
    content: [
      'מוסונייט ויהלום יכולים להיראות דומים במבט ראשון, אבל מדובר בשתי אבני חן שונות מבחינה כימית וגמולוגית.',
      'יהלום מורכב מפחמן. מוסונייט המשמש כיום בתכשיטים מיוצר בתנאי מעבדה ומורכב מסיליקון קרביד. למוסונייט ברק עוצמתי ומשחקי אור בולטים במיוחד.',
      'מוסונייט מגיע לקשיות של כ־9.25 בסולם Mohs, ולכן הוא מתאים מאוד גם לתכשיטים שנענדים לעיתים קרובות.',
      'ב־MIELLEY אנחנו מציגים מוסונייט כמוסונייט — לא כיהלום טבעי ולא כיהלום מעבדה. מבחינתנו הבחירה החכמה מתחילה במידע ברור.',
    ],
  },

  {
    id: 'why-moissanite-sparkles',
    category: 'moissanite',
    categoryLabel: 'מוסונייט',
    eyebrow: 'THE SPARKLE',
    title: 'למה מוסונייט נוצץ כל כך?',
    excerpt:
      'הסיבה שהאבן מושכת כל כך הרבה אור — ואיך זה משפיע על המראה של התכשיט.',
    image: '/assets/luvera-v120-mint-jewelry.webp',
    readTime: '4 דקות קריאה',
    content: [
      'אחד הדברים הראשונים שמבחינים בהם במוסונייט הוא האופן שבו הוא מגיב לאור.',
      'האבן מחזירה אור בעוצמה ומייצרת הבזקי צבע שמעניקים לה נוכחות ברורה גם בתאורה טבעית וגם בתאורת ערב.',
      'זו בדיוק הסיבה שמוסונייט עובד מצוין בסוליטרים, עגילי סטאד, צמידי טניס ותליונים שבהם האבן היא המרכז.',
      'למי שאוהבת תכשיט שנראה נקי אבל עדיין מושך את העין — מוסונייט הוא שילוב מעניין במיוחד.',
    ],
  },

  {
    id: 'expensive-look',
    category: 'style',
    categoryLabel: 'סטייל',
    eyebrow: 'LOOK EXPENSIVE',
    title: '7 דרכים לגרום ללוק להיראות יקר יותר',
    excerpt:
      'לפעמים לא צריך יותר תכשיטים. צריך לבחור ולשלב אותם נכון.',
    image: '/assets/luvera-v120-edit-jewelry.webp',
    readTime: '6 דקות קריאה',
    content: [
      'לוק יוקרתי כמעט אף פעם לא מתחיל בכמות. הוא מתחיל בבחירה.',
      'נסי לבחור נקודת מוקד אחת: זוג עגילים, שרשרת מרכזית או טבעת עם נוכחות — ולבנות סביבה את שאר התכשיטים.',
      'שילוב של פריטים עדינים עם פריט אחד בולט יוצר מראה מדויק יותר מאשר כמה פריטים שמתחרים זה בזה.',
      'גם עקביות במתכת, מרווחים נכונים בין שרשראות ושימוש בפרופורציות שונות יכולים להפוך לוק פשוט להרבה יותר מוקפד.',
    ],
  },

  {
    id: 'stone-shape',
    category: 'guides',
    categoryLabel: 'מדריכים',
    eyebrow: 'STONE SHAPE GUIDE',
    title: 'איזו צורת אבן מתאימה לך?',
    excerpt:
      'עגול, אובל, אמרלד, רדיאנט או טיפה — לכל צורה יש אופי משלה.',
    image: '/assets/moissanite-category-rings-v43.png',
    readTime: '5 דקות קריאה',
    content: [
      'צורת האבן משפיעה מאוד על האופי של הטבעת, גם כאשר גודל האבן והמתכת נשארים זהים.',
      'עגול הוא קלאסי ועל־זמני. אובל יוצר מראה מוארך ואלגנטי. אמרלד מרגיש נקי וגיאומטרי יותר.',
      'רדיאנט נותן שילוב של קווים מודרניים עם הרבה ברק, וטיפה יוצרת מראה רך ומעט דרמטי יותר.',
      'אין צורה “נכונה”. הבחירה הטובה ביותר היא זו שמתאימה לסגנון האישי ולתחושה שאת רוצה לקבל מהתכשיט.',
    ],
  },

  {
    id: 'layering-necklaces',
    category: 'style',
    categoryLabel: 'סטייל',
    eyebrow: 'LAYERING GUIDE',
    title: 'איך משלבים שרשראות בשכבות',
    excerpt:
      'המרווחים, האורכים והפרופורציות שעושים את כל ההבדל.',
    image: '/assets/moissanite-category-necklaces-v43.png',
    readTime: '4 דקות קריאה',
    content: [
      'שילוב שרשראות טוב צריך להיראות טבעי, אבל בדרך כלל יש מאחוריו מעט תכנון.',
      'התחילי בהפרש ברור באורך בין השרשראות כדי שכל אחת תקבל מקום משלה.',
      'אפשר לשלב שרשרת עדינה מאוד עם תליון מרכזי מעט גדול יותר, או להשתמש בשלוש שרשראות דקות באורכים שונים.',
      'אם כולן נוצצות מאוד, שמרי על עיצובים נקיים. אם אחת בולטת, אפשר לתת לאחרות תפקיד משלים.',
    ],
  },

  {
    id: 'inside-mielley',
    category: 'stories',
    categoryLabel: 'MIELLEY',
    eyebrow: 'INSIDE MIELLEY',
    title: 'למה בחרנו במוסונייט',
    excerpt:
      'הסיפור שמאחורי MIELLEY והרעיון של תכשיט שנראה יוקרתי ומרגיש חכם.',
    image: '/assets/new-arrivals-hero.png',
    readTime: '5 דקות קריאה',
    content: [
      'MIELLEY נבנתה סביב רעיון פשוט: תכשיט צריך לגרום לך להרגיש טוב כשאת עונדת אותו — ולא רק כשהוא נמצא בקופסה.',
      'בחרנו במוסונייט בזכות המראה, הברק והאפשרות ליצור תכשיטים עם נוכחות מבלי להפוך את הבחירה למשהו כבד או מאיים.',
      'אנחנו אוהבים קווים נקיים, כסף 925, גימורים בהירים ועיצובים שאפשר לענוד גם ביום רגיל ולא רק באירוע.',
      'LOOK EXPENSIVE. THINK SMART. הוא לא רק משפט. הוא הדרך שבה אנחנו רוצים לבנות את הקולקציה ואת חוויית הקנייה כולה.',
    ],
  },
];

const categories: { key: JournalCategory; label: string }[] = [
  { key: 'all', label: 'הכול' },
  { key: 'style', label: 'סטייל' },
  { key: 'moissanite', label: 'מוסונייט' },
  { key: 'guides', label: 'מדריכים' },
  { key: 'stories', label: 'MIELLEY STORIES' },
];

export function JournalPage() {
  const [category, setCategory] = useState<JournalCategory>('all');
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);

  const visibleArticles =
    category === 'all'
      ? articles
      : articles.filter((article) => article.category === category);

  const openArticle = (article: Article) => {
    setSelectedArticle(article);
  };

  return (
    <main className="mj-page" dir="rtl">

      {/* HERO */}
      <section className="mj-hero">
        <div className="mj-hero-media">
         <img
  src="/assets/luvera-created-with-intention.webp"
  alt="MIELLEY Journal"
/>
        </div>

        <div className="mj-hero-copy">
          <span className="mj-kicker">
            MIELLEY JOURNAL
          </span>

          <div className="mj-hero-meta">
            STYLE · MOISSANITE · GUIDES · STORIES
          </div>

          <h1>
            קצת יותר
            <em> מברק.</em>
          </h1>

          <p>
            מדריכים, השראה ורעיונות שיעזרו לך לבחור,
            לשלב ולענוד תכשיטים בדרך שלך.
          </p>

          <button
            type="button"
            className="mj-hero-button"
            onClick={() => openArticle(articles[0])}
          >
            לכתבה הנבחרת
            <span>←</span>
          </button>
        </div>
      </section>


      {/* CATEGORY NAV */}
      <section className="mj-nav">
        <div className="container mj-nav-inner">
          {categories.map((item) => (
            <button
              key={item.key}
              type="button"
              className={category === item.key ? 'active' : ''}
              onClick={() => setCategory(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </section>


      {/* FEATURED */}
      {category === 'all' && (
        <section className="mj-featured container">

          <div className="mj-section-heading">
            <span>FEATURED STORY</span>
            <h2>הכתבה הנבחרת</h2>
          </div>

          <article
            className="mj-featured-card"
            onClick={() => openArticle(articles[0])}
          >
            <div className="mj-featured-image">
              <img
                src={articles[0].image}
                alt={articles[0].title}
              />
            </div>

            <div className="mj-featured-copy">
              <span>
                {articles[0].eyebrow}
              </span>

              <h3>
                {articles[0].title}
              </h3>

              <p>
                {articles[0].excerpt}
              </p>

              <small>
                {articles[0].readTime}
              </small>

              <b>
                לקריאה
                <i>←</i>
              </b>
            </div>
          </article>
        </section>
      )}


      {/* ARTICLES */}
      <section className="mj-articles container">

        <div className="mj-section-heading mj-section-heading-row">
          <div>
            <span>
              THE JOURNAL EDIT
            </span>

            <h2>
              לקרוא. לגלות. לבחור.
            </h2>
          </div>

          <p>
            השראה, סטייל וידע שימושי —
            בלי מילים מסובכות ובלי רעש.
          </p>
        </div>

        <div className="mj-grid">
          {visibleArticles.map((article, index) => (
            <article
              key={article.id}
              className={`mj-card mj-card-${(index % 3) + 1}`}
              onClick={() => openArticle(article)}
            >
              <div className="mj-card-image">
                <img
                  src={article.image}
                  alt={article.title}
                />

                <span className="mj-card-category">
                  {article.categoryLabel}
                </span>
              </div>

              <div className="mj-card-copy">
                <small>
                  {article.eyebrow}
                </small>

                <h3>
                  {article.title}
                </h3>

                <p>
                  {article.excerpt}
                </p>

                <div className="mj-card-bottom">
                  <span>
                    {article.readTime}
                  </span>

                  <b>
                    READ STORY ←
                  </b>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>


      {/* SMART SPARKLE */}
      <section className="mj-smart">
        <div className="container">

          <div className="mj-smart-head">
            <span>
              THE SMART SPARKLE EDIT
            </span>

            <h2>
              לבחור חכם.
              <br />
              להיראות מעולה.
            </h2>
          </div>

          <div className="mj-smart-grid">

            <button
              type="button"
              onClick={() => navigate('/ring-builder')}
            >
              <span>01</span>
              <strong>THE RING EDIT</strong>
              <h3>לבנות את הטבעת שלך</h3>
              <p>
                סגנון, מתכת, צורת אבן וגודל —
                שלב אחר שלב.
              </p>
              <b>להתחיל לבנות ←</b>
            </button>

            <button
              type="button"
              onClick={() => navigate('/size-guide')}
            >
              <span>02</span>
              <strong>SIZE GUIDE</strong>
              <h3>למצוא את המידה הנכונה</h3>
              <p>
                מדריך פשוט לבחירת מידת טבעת
                לפני ההזמנה.
              </p>
              <b>למדריך המידות ←</b>
            </button>

            <button
              type="button"
              onClick={() => navigate('/catalog')}
            >
              <span>03</span>
              <strong>THE COLLECTION</strong>
              <h3>לגלות את הקולקציה</h3>
              <p>
                טבעות, עגילים, שרשראות וצמידים
                עם הרבה ברק.
              </p>
              <b>לכל התכשיטים ←</b>
            </button>

          </div>
        </div>
      </section>


      {/* MOST READ */}
      <section className="mj-most-read">
        <div className="container mj-most-read-inner">

          <div className="mj-most-title">
            <span>MOST READ</span>
            <h2>מה כולם קוראים עכשיו.</h2>
          </div>

          <div className="mj-most-list">
            {articles.slice(0, 5).map((article, index) => (
              <button
                type="button"
                key={article.id}
                onClick={() => openArticle(article)}
              >
                <span>
                  {String(index + 1).padStart(2, '0')}
                </span>

                <strong>
                  {article.title}
                </strong>

                <i>
                  ←
                </i>
              </button>
            ))}
          </div>

        </div>
      </section>


      {/* FINAL */}
      <section className="mj-final">
        <span>
          MIELLEY
        </span>

        <h2>
          קראת. עכשיו תורך לזהור.
        </h2>

        <p>
          לגלות את התכשיטים מאחורי הסיפורים.
        </p>

        <button
          type="button"
          onClick={() => navigate('/catalog')}
        >
          לכל הקולקציה
          <span>←</span>
        </button>
      </section>


      {/* ARTICLE MODAL */}
      {selectedArticle && (
        <div
          className="mj-modal-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setSelectedArticle(null);
            }
          }}
        >
          <article className="mj-modal">

            <button
              type="button"
              className="mj-modal-close"
              onClick={() => setSelectedArticle(null)}
              aria-label="סגירה"
            >
              ×
            </button>

            <div className="mj-modal-image">
              <img
                src={selectedArticle.image}
                alt={selectedArticle.title}
              />
            </div>

            <div className="mj-modal-body">
              <span>
                {selectedArticle.eyebrow}
              </span>

              <h2>
                {selectedArticle.title}
              </h2>

              <div className="mj-modal-readtime">
                {selectedArticle.readTime}
              </div>

              {selectedArticle.content.map((paragraph, index) => (
                <p key={index}>
                  {paragraph}
                </p>
              ))}

              <div className="mj-modal-signature">
                <span>MIELLEY JOURNAL</span>
                <b>LOOK EXPENSIVE. THINK SMART.</b>
              </div>

              <div className="mj-modal-actions">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedArticle(null);
                    navigate('/catalog');
                  }}
                >
                  לגלות את הקולקציה
                  <span>←</span>
                </button>

                <button
                  type="button"
                  onClick={() => setSelectedArticle(null)}
                >
                  חזרה למגזין
                </button>
              </div>
            </div>
          </article>
        </div>
      )}

    </main>
  );
}